import type { DashboardDto, ProjectStageDto, StageRequirementDto } from '@project-os/contracts';
import type {
  CreateRequirementInput,
  CreateStageInput,
  ReorderStagesInput,
  UpdateRequirementInput,
  UpdateStageInput,
} from '@project-os/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from './client';
import { projectKeys } from './projects';

export const stageKeys = {
  list: (projectId: string) => ['projects', projectId, 'stages'] as const,
  dashboard: (projectId: string) => ['projects', projectId, 'dashboard'] as const,
};

export function useStages(projectId: string | undefined) {
  return useQuery({
    queryKey: stageKeys.list(projectId ?? ''),
    queryFn: ({ signal }) =>
      apiRequest<ProjectStageDto[]>(`/projects/${projectId}/stages`, { signal }),
    enabled: Boolean(projectId),
  });
}

/** Обзор проекта. Один запрос — весь экран. */
export function useDashboard(projectId: string | undefined) {
  return useQuery({
    queryKey: stageKeys.dashboard(projectId ?? ''),
    queryFn: ({ signal }) =>
      apiRequest<DashboardDto>(`/projects/${projectId}/dashboard`, { signal }),
    enabled: Boolean(projectId),
    staleTime: 30_000,
  });
}

/**
 * Изменения этапов возвращают полный список — кэш обновляется ответом.
 * Обзор и карточка проекта перезапрашиваются: готовность этапа видна и там.
 */
function useStageMutation<TInput>(
  projectId: string,
  request: (input: TInput) => Promise<ProjectStageDto[]>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: (stages) => {
      queryClient.setQueryData(stageKeys.list(projectId), stages);
      void queryClient.invalidateQueries({ queryKey: stageKeys.dashboard(projectId) });
      void queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      void queryClient.invalidateQueries({ queryKey: projectKeys.all });
    },
  });
}

export function useCreateStage(projectId: string) {
  return useStageMutation(projectId, (input: CreateStageInput) =>
    apiRequest<ProjectStageDto[]>(`/projects/${projectId}/stages`, {
      method: 'POST',
      body: input,
    }),
  );
}

export function useUpdateStage(projectId: string) {
  return useStageMutation(projectId, (input: UpdateStageInput & { stageId: string }) => {
    const { stageId, ...body } = input;
    return apiRequest<ProjectStageDto[]>(`/projects/${projectId}/stages/${stageId}`, {
      method: 'PATCH',
      body,
    });
  });
}

export function useReorderStages(projectId: string) {
  return useStageMutation(projectId, (input: ReorderStagesInput) =>
    apiRequest<ProjectStageDto[]>(`/projects/${projectId}/stages/order`, {
      method: 'PATCH',
      body: input,
    }),
  );
}

/**
 * Переход к этапу. Возвращает и обновлённые этапы, и список невыполненных
 * требований прежнего этапа — интерфейс показывает их после перехода.
 */
export function useMakeStageCurrent(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (stageId: string) =>
      apiRequest<{ stages: ProjectStageDto[]; unfinishedRequirements: StageRequirementDto[] }>(
        `/projects/${projectId}/stages/${stageId}/make-current`,
        { method: 'POST' },
      ),
    onSuccess: (result) => {
      queryClient.setQueryData(stageKeys.list(projectId), result.stages);
      void queryClient.invalidateQueries({ queryKey: stageKeys.dashboard(projectId) });
      void queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      void queryClient.invalidateQueries({ queryKey: projectKeys.all });
    },
  });
}

export function useCreateRequirement(projectId: string) {
  return useStageMutation(projectId, (input: CreateRequirementInput & { stageId: string }) => {
    const { stageId, ...body } = input;
    return apiRequest<ProjectStageDto[]>(`/projects/${projectId}/stages/${stageId}/requirements`, {
      method: 'POST',
      body,
    });
  });
}

export function useUpdateRequirement(projectId: string) {
  return useStageMutation(
    projectId,
    (input: UpdateRequirementInput & { requirementId: string }) => {
      const { requirementId, ...body } = input;
      return apiRequest<ProjectStageDto[]>(`/projects/${projectId}/requirements/${requirementId}`, {
        method: 'PATCH',
        body,
      });
    },
  );
}

export function useRemoveRequirement(projectId: string) {
  return useStageMutation(projectId, (requirementId: string) =>
    apiRequest<ProjectStageDto[]>(`/projects/${projectId}/requirements/${requirementId}`, {
      method: 'DELETE',
    }),
  );
}

export function useLinkRequirementItem(projectId: string) {
  return useStageMutation(projectId, (input: { requirementId: string; itemId: string }) =>
    apiRequest<ProjectStageDto[]>(
      `/projects/${projectId}/requirements/${input.requirementId}/items`,
      { method: 'POST', body: { itemId: input.itemId } },
    ),
  );
}

export function useUnlinkRequirementItem(projectId: string) {
  return useStageMutation(projectId, (input: { requirementId: string; itemId: string }) =>
    apiRequest<ProjectStageDto[]>(
      `/projects/${projectId}/requirements/${input.requirementId}/items/${input.itemId}`,
      { method: 'DELETE' },
    ),
  );
}
